import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { uiTypes } from 'shared-components';

import { AGENT_DETAIL_FIELD_LABELS } from 'types';

import { ApplicationState } from '../../../store';

type NameFieldId = 'agentFamilyName' | 'agentGivenName';

export const NAME_FIELD_CONFIG: Array<{ id: NameFieldId; label: string }> = [
  { id: 'agentFamilyName', label: AGENT_DETAIL_FIELD_LABELS.agentFamilyName },
  { id: 'agentGivenName', label: AGENT_DETAIL_FIELD_LABELS.agentGivenName },
];

export function splitAddressLines(text: string): { line1: string; line2: string; line3: string } {
  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  return {
    line1: lines[0] ?? '',
    line2: lines[1] ?? '',
    line3: lines.slice(2).join(', '),
  };
}

interface CheckCompleteParams {
  nameFields: Record<NameFieldId, string>;
  offerNameValues: Record<NameFieldId, string | null | undefined>;
  nameApproved: NameFieldId[];
  nameEdited: NameFieldId[];
  hasAnyAddressImport: boolean;
  addressImportApproved: boolean;
  addressImportEdited: boolean;
  address: { homeAddress: string; city: string; postCode: string; country: string };
}

export function checkAgentDetailsComplete(params: CheckCompleteParams): boolean {
  const nameFieldsReady = NAME_FIELD_CONFIG.every(({ id }) => {
    const hasValue = !!params.nameFields[id].trim();
    if (!hasValue) return false;
    if (!params.offerNameValues[id]) return true;
    return params.nameApproved.includes(id) || params.nameEdited.includes(id);
  });
  const addressImportHandled =
    !params.hasAnyAddressImport || params.addressImportApproved || params.addressImportEdited;
  const hasRequiredAddressData =
    !!params.address.homeAddress.trim() && !!params.address.city.trim() && !!params.address.country.trim();
  return nameFieldsReady && addressImportHandled && hasRequiredAddressData;
}

interface AddressState {
  homeAddress: string;
  city: string;
  postCode: string;
  country: string;
}

const EMPTY_ADDRESS: AddressState = { homeAddress: '', city: '', postCode: '', country: '' };

interface AgentDetailsState {
  offer: ApplicationState['student']['offer'];
  summary: ApplicationState['student']['summary'];
  isPageComplete: boolean | undefined;
  displayName: string;
  isPending: boolean;
  isApproved: boolean;
  agentApprovedLocally: boolean;
  handleApproveAgentLocally: () => void;
  nameFields: Record<NameFieldId, string>;
  nameApproved: NameFieldId[];
  nameEdited: NameFieldId[];
  address: AddressState;
  draftAddress: AddressState;
  addressImportApproved: boolean;
  addressImportEdited: boolean;
  showModal: boolean;
  handleApproveNameField: (fieldId: NameFieldId) => void;
  handleEditNameField: (fieldId: NameFieldId) => void;
  handleUndoNameField: (fieldId: NameFieldId) => void;
  handleNameFieldChange: (fieldId: NameFieldId, value: string) => void;
  handleAddressImportApprove: () => void;
  handleAddressImportEdit: () => void;
  handleAddressImportUndo: () => void;
  setDraftAddress: React.Dispatch<React.SetStateAction<AddressState>>;
  openAddressModal: () => void;
  closeAddressModal: () => void;
  saveAndCloseAddressModal: () => void;
  handleRemoveAddress: () => void;
  isComplete: () => boolean;
  hasAnyAddressImportValue: () => boolean;
  getImportAddress: () => string;
  getAddressCardItems: () => uiTypes.ICardItem[];
}

export function useAgentDetailsState(): AgentDetailsState {
  const offer = useSelector(({ student }: ApplicationState) => student.offer);
  const summary = useSelector(({ student }: ApplicationState) => student.summary);
  const isPageComplete = offer.isAgentDetailsConfirmed;

  const displayName = offer.assignedAgents?.[0]?.displayName ?? '';
  const isPending = !offer.isAgentConfirmed && !summary?.conflicts?.agentDisplayName;
  const isApproved = offer.isAgentConfirmed && !summary?.conflicts?.agentDisplayName;

  function getImportAddress(): string {
    return [offer.branchAddressLine1, offer.branchAddressLine2, offer.branchAddressLine3]
      .filter(Boolean)
      .map(s => String(s))
      .join('\n');
  }

  function hasAnyAddressImportValue(): boolean {
    return !!(
      offer.branchAddressLine1 ||
      offer.branchAddressLine2 ||
      offer.branchAddressLine3 ||
      offer.branchCity ||
      offer.branchCountry ||
      offer.branchPostcode
    );
  }

  const [nameFields, setNameFields] = useState<Record<NameFieldId, string>>(() => ({
    agentFamilyName: String(offer.agentFamilyName ?? ''),
    agentGivenName: String(offer.agentGivenName ?? ''),
  }));
  const [nameApproved, setNameApproved] = useState<NameFieldId[]>(() => {
    if (!isPageComplete) return [];
    return NAME_FIELD_CONFIG.filter(({ id }) => !!offer[id]).map(({ id }) => id);
  });
  const [nameEdited, setNameEdited] = useState<NameFieldId[]>([]);

  const [address, setAddress] = useState({
    homeAddress: getImportAddress(),
    city: String(offer.branchCity ?? ''),
    postCode: String(offer.branchPostcode ?? ''),
    country: String(offer.branchCountry ?? ''),
  });
  const [addressImportApproved, setAddressImportApproved] = useState<boolean>(
    () => !!isPageComplete && hasAnyAddressImportValue(),
  );
  const [addressImportEdited, setAddressImportEdited] = useState<boolean>(false);
  const [agentApprovedLocally, setAgentApprovedLocally] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [draftAddress, setDraftAddress] = useState<AddressState>(EMPTY_ADDRESS);

  function handleApproveAgentLocally(): void {
    setAgentApprovedLocally(true);
  }

  function handleApproveNameField(fieldId: NameFieldId): void {
    setNameApproved(prev => [...prev, fieldId]);
    setNameEdited(prev => prev.filter(f => f !== fieldId));
  }

  function handleEditNameField(fieldId: NameFieldId): void {
    setNameApproved(prev => prev.filter(f => f !== fieldId));
    setNameEdited(prev => [...prev, fieldId]);
  }

  function handleUndoNameField(fieldId: NameFieldId): void {
    setNameApproved(prev => prev.filter(f => f !== fieldId));
    setNameEdited(prev => prev.filter(f => f !== fieldId));
    setNameFields(prev => ({ ...prev, [fieldId]: String(offer[fieldId] ?? '') }));
  }

  function handleNameFieldChange(fieldId: NameFieldId, value: string): void {
    setNameFields(prev => ({ ...prev, [fieldId]: value }));
  }

  function handleAddressImportApprove(): void {
    setAddressImportApproved(true);
    setAddressImportEdited(false);

    const isEdited =
      addressImportEdited ||
      address.homeAddress !== getImportAddress() ||
      address.city !== String(offer.branchCity ?? '') ||
      address.country !== String(offer.branchCountry ?? '') ||
      address.postCode !== String(offer.branchPostcode ?? '');

    if (!isEdited) {
      setAddress({
        homeAddress: getImportAddress(),
        city: String(offer.branchCity ?? ''),
        country: String(offer.branchCountry ?? ''),
        postCode: String(offer.branchPostcode ?? ''),
      });
    }
  }

  function handleAddressImportEdit(): void {
    setAddressImportApproved(false);
    setAddressImportEdited(true);
  }

  function handleAddressImportUndo(): void {
    setAddressImportApproved(false);
    setAddressImportEdited(false);
    setAddress({
      homeAddress: getImportAddress(),
      city: String(offer.branchCity ?? ''),
      postCode: String(offer.branchPostcode ?? ''),
      country: String(offer.branchCountry ?? ''),
    });
  }

  function openAddressModal(): void {
    setDraftAddress({ ...address });
    setShowModal(true);
  }

  function closeAddressModal(): void {
    setShowModal(false);
  }

  function saveAndCloseAddressModal(): void {
    setAddress({ ...draftAddress });
    handleAddressImportEdit();
    setShowModal(false);
  }

  function handleRemoveAddress(): void {
    setAddress({ ...EMPTY_ADDRESS });
    setAddressImportEdited(false);
  }

  function isComplete(): boolean {
    return checkAgentDetailsComplete({
      nameFields,
      offerNameValues: {
        agentFamilyName: offer.agentFamilyName,
        agentGivenName: offer.agentGivenName,
      },
      nameApproved,
      nameEdited,
      hasAnyAddressImport: hasAnyAddressImportValue(),
      addressImportApproved,
      addressImportEdited,
      address,
    });
  }

  function getAddressCardItems(): uiTypes.ICardItem[] {
    return [
      { label: 'Address', value: address.homeAddress, required: true },
      { label: 'City', value: address.city, required: true },
      { label: 'Postcode', value: address.postCode, required: false },
      { label: 'Country', value: address.country, required: true },
    ];
  }

  return {
    offer,
    summary,
    isPageComplete,
    displayName,
    isPending,
    isApproved,
    agentApprovedLocally,
    handleApproveAgentLocally,
    nameFields,
    nameApproved,
    nameEdited,
    address,
    draftAddress,
    addressImportApproved,
    addressImportEdited,
    showModal,
    handleApproveNameField,
    handleEditNameField,
    handleUndoNameField,
    handleNameFieldChange,
    handleAddressImportApprove,
    handleAddressImportEdit,
    handleAddressImportUndo,
    setDraftAddress,
    openAddressModal,
    closeAddressModal,
    saveAndCloseAddressModal,
    handleRemoveAddress,
    isComplete,
    hasAnyAddressImportValue,
    getImportAddress,
    getAddressCardItems,
  };
}
